import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce, isEqual } from "lodash-es";
import { useTranslation } from "react-i18next";
import type { InferType } from "yup";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type { ManagedLocationBase } from "@thalamos/aspire-common";
import {
  canAmhpSearchS12Doctors,
  isAdminUser,
  isAmhpAccreditation,
  isIndependentUserSession,
  isSection12Accreditation,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { config } from "../../config.js";
import { LoggedInUserContext } from "../../Contexts.js";
import {
  applyFormInvariants,
  APPROVAL_TYPES,
  createAccreditations,
  dataSchema,
  PROF_REG_TYPES,
} from "./userProfileHelpers.js";
import {
  AddressField,
  Banner,
  BannerList,
  Button,
  Dropdown,
  FormLabel,
  FormTitle,
  ReadOnlyContent,
  renderErrorToast,
  renderSuccessToast,
  TextField,
  Typeahead,
} from "~/components/design-system/index.js";
import {
  DateFormField,
  HorizontalLine,
  Signature,
} from "~/components/form/index.js";
import { Container } from "~/components/layout/styleWrappers.js";
import { S12VerificationOverlay } from "~/components/S12VerificationOverlay/S12VerificationOverlay.js";
import { ThalamosLink } from "~/components/ThalamosLink.js";
import {
  useS12ApprovalCheck,
  type S12ApprovalCheckResult,
} from "~/hooks/useS12ApprovalCheck.js";
import { routeFns } from "~/routePaths.js";
import { formatToE164, isValidE164 } from "~/utils/phone-utils.js";

export function applyS12ApprovalCheckResultToValues(
  values: InferType<typeof dataSchema>,
  result: S12ApprovalCheckResult,
): InferType<typeof dataSchema> {
  const isApprovedClinician = values.mhApprovals?.includes(APPROVAL_TYPES.AC);

  if (result.status !== "found") {
    return {
      ...values,
      section12SyncedWithApprovalsApi: false,
      ...(isApprovedClinician && {
        approvedClinicianSyncedWithApprovalsApi: false,
      }),
    };
  }

  const mhApprovals = values.mhApprovals?.includes(APPROVAL_TYPES.S12)
    ? values.mhApprovals
    : [...(values.mhApprovals ?? []), APPROVAL_TYPES.S12];
  const matchesCurrentExpiry =
    !!result.section12ExpiryDate &&
    values.section12ExpiryDate === result.section12ExpiryDate;

  const localDateInvalid =
    !values.section12ExpiryDate ||
    !dayjs(values.section12ExpiryDate as string).isValid();
  const useApiDate =
    result.section12ExpiryDate && (result.updated || localDateInvalid);

  const s12Synced =
    result.updated ||
    matchesCurrentExpiry ||
    (localDateInvalid && !!useApiDate);

  // When the user holds AC, the API date also covers AC
  const acUpdates: Partial<InferType<typeof dataSchema>> = {};
  if (isApprovedClinician) {
    const acLocalInvalid =
      !values.approvedClinicianExpiryDate ||
      !dayjs(values.approvedClinicianExpiryDate as string).isValid();
    const acMatchesCurrent =
      !!result.section12ExpiryDate &&
      values.approvedClinicianExpiryDate === result.section12ExpiryDate;
    const useApiDateForAc =
      result.section12ExpiryDate && (result.updated || acLocalInvalid);

    acUpdates.approvedClinicianExpiryDate = useApiDateForAc
      ? result.section12ExpiryDate!
      : values.approvedClinicianExpiryDate;
    acUpdates.approvedClinicianSyncedWithApprovalsApi =
      result.updated ||
      acMatchesCurrent ||
      (acLocalInvalid && !!useApiDateForAc);
  }

  return {
    ...values,
    mhApprovals,
    section12ExpiryDate: useApiDate
      ? result.section12ExpiryDate!
      : values.section12ExpiryDate,
    section12SyncedWithApprovalsApi: s12Synced,
    ...acUpdates,
  };
}

export const UserProfileContainer = ({
  values,
  user,
  errors,
  setValues,
  derived,
  localAuthorities,
  fieldProps,
  dispatchAutoSaveState,
  isAutoSaving,
  defaultSelectedContextName,
}: {
  // TODO: type as FormikHelpers<InferType<typeof dataSchema>>["setValues"],
  // then fix the unawaited promises and type errors that type exposes.
  setValues: (...args: any[]) => unknown;
  [prop: string]: any;
}) => {
  const userContext = useContext(LoggedInUserContext);
  const { t } = useTranslation();

  // Ref holds the latest save implementation so the debounced wrapper
  // always calls through fresh closures without being recreated each render.
  const autoSaveImplRef = useRef<
    (userData: InferType<typeof dataSchema>) => Promise<void>
  >(async () => {});

  autoSaveImplRef.current = async (userData: InferType<typeof dataSchema>) => {
    const accreditations = createAccreditations(userData);
    const userDataValues = {
      name: userData.name,
      address: {
        address: userData.address,
        postalCode: userData.postalCode?.trim(),
      },
      defaultSignature: userData.defaultSignature,
      accreditations,
    };
    try {
      const result = await api.users.update(user.id, userDataValues);

      if (result.status === 204) {
        dispatchAutoSaveState("success");
      } else if (result.status === 404) {
        return;
      } else {
        dispatchAutoSaveState("error");
      }
    } catch {
      dispatchAutoSaveState("error");
    }
  };

  // Stable debounced wrapper — created once, delegates to the ref
  const autoSave = useMemo(
    () =>
      debounce((userData: InferType<typeof dataSchema>) => {
        void autoSaveImplRef.current(userData);
      }, 2000),
    [],
  );

  const [lastSavedValues, setLastSavedValues] = useState(fieldProps.values);

  // Phone number state (managed independently from the Formik form)
  const isAmhp = userContext?.user?.accreditations?.some(isAmhpAccreditation);
  const amhpConfiguration = userContext?.user?.sessionOrganisationConfiguration;
  const isDrFinderEnabled =
    amhpConfiguration != null && canAmhpSearchS12Doctors(amhpConfiguration);
  const showPhoneField = isAmhp && isDrFinderEnabled;

  const [phoneInput, setPhoneInput] = useState(user.phone ?? "");
  const [savedPhone, setSavedPhone] = useState(user.phone ?? "");
  const [phoneConsent, setPhoneConsent] = useState(!!user.phone);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"input" | "saved">(
    user.phone ? "saved" : "input",
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // S12 approval check on GMC number change
  const { check: checkS12 } = useS12ApprovalCheck();
  const [isCheckingS12, setIsCheckingS12] = useState(false);
  const [s12Result, setS12Result] = useState<S12ApprovalCheckResult | null>(
    null,
  );
  const [showS12Overlay, setShowS12Overlay] = useState(false);
  const previousGmcRef = useRef<string | undefined>(values.gmcNumber);
  const activeS12CheckRequestRef = useRef(0);

  useEffect(() => {
    const currentGmc = values.gmcNumber as string | undefined;
    const previousGmc = previousGmcRef.current;
    previousGmcRef.current = currentGmc;

    if (!currentGmc || currentGmc.length < 7) {
      setShowS12Overlay(false);
      setIsCheckingS12(false);
      setS12Result(null);
      return;
    }

    if (currentGmc === previousGmc) {
      return;
    }

    activeS12CheckRequestRef.current += 1;
    const requestId = activeS12CheckRequestRef.current;

    setShowS12Overlay(true);
    setIsCheckingS12(true);
    setS12Result(null);

    void checkS12(user.id, currentGmc).then((result) => {
      if (requestId !== activeS12CheckRequestRef.current) {
        return;
      }

      setS12Result(result);
      setIsCheckingS12(false);

      setValues((prev: InferType<typeof dataSchema>) => {
        const isAc = prev.mhApprovals?.includes(APPROVAL_TYPES.AC);
        const localDate = (
          isAc ? prev.approvedClinicianExpiryDate : prev.section12ExpiryDate
        ) as string | undefined;
        const isDateConflict =
          result.status === "found" &&
          !!result.section12ExpiryDate &&
          !!localDate &&
          dayjs(localDate).isValid() &&
          result.section12ExpiryDate !== localDate;

        if (isDateConflict) {
          return prev;
        }

        return applyS12ApprovalCheckResultToValues(prev, result);
      });
    });
  }, [values.gmcNumber, user.id, checkS12, setValues]);

  const handleCloseS12Overlay = useCallback(() => {
    setShowS12Overlay(false);
  }, []);

  const handleReverifyS12 = useCallback(() => {
    const currentGmc = values.gmcNumber as string | undefined;
    if (!currentGmc || currentGmc.length < 7) return;

    activeS12CheckRequestRef.current += 1;
    const requestId = activeS12CheckRequestRef.current;

    setShowS12Overlay(true);
    setIsCheckingS12(true);
    setS12Result(null);

    void checkS12(user.id, currentGmc).then((result) => {
      if (requestId !== activeS12CheckRequestRef.current) return;

      setS12Result(result);
      setIsCheckingS12(false);

      setValues((prev: InferType<typeof dataSchema>) => {
        const isAc = prev.mhApprovals?.includes(APPROVAL_TYPES.AC);
        const localDate = (
          isAc ? prev.approvedClinicianExpiryDate : prev.section12ExpiryDate
        ) as string | undefined;
        const isDateConflict =
          result.status === "found" &&
          !!result.section12ExpiryDate &&
          !!localDate &&
          dayjs(localDate).isValid() &&
          result.section12ExpiryDate !== localDate;

        if (isDateConflict) {
          return prev;
        }

        return applyS12ApprovalCheckResultToValues(prev, result);
      });
    });
  }, [values.gmcNumber, user.id, checkS12, setValues]);

  const handleExpiryDateAccepted = useCallback(
    (value: Dayjs | null) => {
      if (!value || !value.isValid()) return;
      if (value.isBefore(dayjs(), "day")) return;
      const currentGmc = values.gmcNumber as string | undefined;
      if (!currentGmc || currentGmc.length < 7) return;
      handleReverifyS12();
    },
    [values.gmcNumber, handleReverifyS12],
  );

  const isAcUser = values.mhApprovals?.includes(APPROVAL_TYPES.AC);
  const localExpiryDate = (
    isAcUser ? values.approvedClinicianExpiryDate : values.section12ExpiryDate
  ) as string | undefined;
  const isS12Conflict =
    s12Result?.status === "found" &&
    !!s12Result.section12ExpiryDate &&
    !!localExpiryDate &&
    dayjs(localExpiryDate).isValid() &&
    s12Result.section12ExpiryDate !== localExpiryDate;

  const handleUseProfileDate = useCallback(() => {
    setValues((prev: InferType<typeof dataSchema>) => {
      const isAc = prev.mhApprovals?.includes(APPROVAL_TYPES.AC);
      return {
        ...prev,
        section12SyncedWithApprovalsApi: false,
        ...(isAc && { approvedClinicianSyncedWithApprovalsApi: false }),
      };
    });
    setShowS12Overlay(false);
  }, [setValues]);

  const handleUseApiDate = useCallback(() => {
    if (!s12Result?.section12ExpiryDate) return;
    setValues((prev: InferType<typeof dataSchema>) => {
      const isAc = prev.mhApprovals?.includes(APPROVAL_TYPES.AC);
      return {
        ...prev,
        section12ExpiryDate: s12Result.section12ExpiryDate,
        section12SyncedWithApprovalsApi: true,
        ...(isAc && {
          approvedClinicianExpiryDate: s12Result.section12ExpiryDate,
          approvedClinicianSyncedWithApprovalsApi: true,
        }),
      };
    });
    setShowS12Overlay(false);
  }, [s12Result?.section12ExpiryDate, setValues]);

  const handleSavePhone = useCallback(async () => {
    const formatted = formatToE164(phoneInput);
    if (!isValidE164(formatted)) {
      setPhoneError(t("pages.userProfile.phone.invalidFormat"));
      return;
    }
    setPhoneError(null);
    setPhoneSaving(true);
    try {
      const result = await api.users.update(user.id, {
        phone: formatted,
      });

      if (result.status === 204) {
        renderSuccessToast({
          message: t("pages.userProfile.phone.savedSuccess"),
        });
        setSavedPhone(formatted);
        setPhoneStep("saved");
      } else {
        renderErrorToast({
          message: t("pages.userProfile.phone.savedError"),
        });
      }
    } catch {
      renderErrorToast({
        message: t("pages.userProfile.phone.savedError"),
      });
    } finally {
      setPhoneSaving(false);
    }
  }, [phoneInput, user.id, t]);

  const handleRemovePhone = useCallback(async () => {
    setPhoneSaving(true);
    try {
      const result = await api.users.update(user.id, { phone: null });

      if (result.status === 204) {
        setPhoneInput("");
        setSavedPhone("");
        setPhoneConsent(false);
        setPhoneStep("input");
        setPhoneError(null);
        renderSuccessToast({
          message: t("pages.userProfile.phone.removedSuccess"),
        });
      } else {
        renderErrorToast({
          message: t("pages.userProfile.phone.removedError"),
        });
      }
    } catch {
      renderErrorToast({
        message: t("pages.userProfile.phone.removedError"),
      });
    } finally {
      setPhoneSaving(false);
    }
  }, [user.id, t]);

  const handleChangePhone = useCallback(() => {
    setPhoneInput(savedPhone);
    setPhoneStep("input");
  }, [savedPhone]);

  // Keep the hidden S12 date in Formik in sync with the AC date whenever the
  // user is RMP+AC. Without this, changing the AC date leaves a stale S12 date
  // in Formik state — which gets persisted if the user later unticks AC, making
  // the S12 field visible again with the old value.
  useEffect(() => {
    if (
      derived.isRmp &&
      derived.isApprovedClinician &&
      values.approvedClinicianExpiryDate !== values.section12ExpiryDate
    ) {
      setValues((prev: InferType<typeof dataSchema>) => ({
        ...prev,
        section12ExpiryDate: prev.approvedClinicianExpiryDate,
      }));
    }
  }, [
    values.approvedClinicianExpiryDate,
    values.section12ExpiryDate,
    derived.isRmp,
    derived.isApprovedClinician,
    setValues,
  ]);

  const validateForm = async (values: any) => {
    try {
      // Validate the form data
      await dataSchema.validate(values, { abortEarly: false });
      // If validation passes, there are no errors
      return true;
    } catch (errors: any) {
      // If validation fails, errors will be caught here
      return errors.errors;
    }
  };

  // Trigger auto-save on field change
  useEffect(() => {
    const validateAndAutoSave = async () => {
      // Validate the form data before making the API call

      const validationResult = await validateForm(fieldProps.values);

      // Check if there are no errors and values have changed
      if (
        validationResult === true &&
        !isEqual(fieldProps.values, lastSavedValues)
      ) {
        // Set auto-saving state to loading
        if (!isAutoSaving) dispatchAutoSaveState("loading");

        // Call autoSave function with the updated values
        autoSave(fieldProps.values);

        // Update the hasErrors state and lastSavedValues
        setLastSavedValues(fieldProps.values);
      }
    };
    validateAndAutoSave();
  }, [fieldProps.values]);

  return (
    <>
      <S12VerificationOverlay
        open={showS12Overlay}
        isChecking={isCheckingS12}
        status={s12Result?.status}
        section12ExpiryDate={s12Result?.section12ExpiryDate}
        isConflict={isS12Conflict}
        localExpiryDate={localExpiryDate}
        onClose={handleCloseS12Overlay}
        onUseProfileDate={handleUseProfileDate}
        onUseApiDate={handleUseApiDate}
      />
      <Container addMarginBottom>
        <Box sx={{ mb: 2 }}>
          <ReadOnlyContent
            isActive
            label={t("common.email")}
            content={[user.email]}
          />
        </Box>
        <TextField
          errorMessage={errors.name as string}
          showHelperText={!!errors.name}
          useFullWidth={true}
          label={t("common.name")}
          name="listItemName"
          value={values.name}
          onChange={(value: string) => setValues({ ...values, name: value })}
        />

        <AddressField
          showHelperText={!!errors.address || !!errors.postalCode}
          errors={errors}
          hideConfirmation={true}
          label={t("pages.userProfile.workAddress")}
          name="userProfileAddress"
          value={{
            address: values.address,
            postalCode: values.postalCode,
          }}
          googleMapsApiKey={config.googleMapsApiKey}
          onChange={(value: { address: string; postalCode: string }) => {
            setValues({
              ...values,
              address: value.address,
              postalCode: value.postalCode,
            });
          }}
          addressCheckBoxLabel={t(
            "forms.nameAddressFormField.addressCheckBoxLabel",
          )}
        />
        {defaultSelectedContextName && (
          <>
            <ReadOnlyContent
              label={t("pages.userProfile.defaultMembership")}
              content={[defaultSelectedContextName]}
              dense
            />
            <ThalamosLink to={routeFns.contextSelector()}>
              <Box sx={{ cursor: "pointer", color: "primary.main", ml: 1.2 }}>
                {t("layout.menu.changeContext")}
              </Box>{" "}
            </ThalamosLink>
          </>
        )}
        {isIndependentUserSession(userContext?.user) &&
          userContext?.user?.accreditations?.some(isSection12Accreditation) && (
            <Box sx={{ mt: 3 }}>
              {(() => {
                const s12Accred = userContext?.user?.accreditations?.find(
                  isSection12Accreditation,
                );
                const expiryDate = s12Accred?.expiryDate
                  ? dayjs(s12Accred.expiryDate)
                  : null;
                const today = dayjs();
                const isExpired =
                  expiryDate && expiryDate.isBefore(today, "day");
                const isExpiringSoon =
                  expiryDate &&
                  !isExpired &&
                  expiryDate.diff(today, "day") <= 30;

                return (
                  <>
                    {(isExpired || isExpiringSoon) && (
                      <Box sx={{ mb: 2 }}>
                        <Banner
                          bannerType={
                            isExpired ? BannerList.ERROR : BannerList.WARNING
                          }
                          title={
                            isExpired
                              ? "Section 12 Accreditation Expired"
                              : "Section 12 Accreditation Expiring Soon"
                          }
                          body={
                            isExpired
                              ? `Your Section 12 accreditation expired on ${expiryDate?.format("D MMMM YYYY")}. Please renew your accreditation.`
                              : `Your Section 12 accreditation expires on ${expiryDate?.format("D MMMM YYYY")}. Please ensure it is renewed before expiry.`
                          }
                        />
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          )}
        <Box sx={{ mt: 3 }}>
          <ThalamosLink
            to={routeFns.favouriteRecipientsPage(user.id)}
            data-testid="favourite-recipients-link"
          >
            <Button
              variant="outlined"
              color="primary"
              label={t("pages.userProfile.manageFavouriteRecipients")}
              testId="favourite-recipients-btn"
            />
          </ThalamosLink>
        </Box>
      </Container>
      {showPhoneField && (
        <Container addMarginBottom data-testid="amhp-phone-section">
          <FormTitle
            useReducedTopPadding
            hasTitleBottomMargin={false}
            subtitleText={t("pages.userProfile.phone.title")}
          />
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {t("pages.userProfile.phone.description")}
          </Typography>

          {phoneStep === "saved" && savedPhone && (
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "success.50",
                  border: 1,
                  borderColor: "success.200",
                }}
              >
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                  {savedPhone}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="text"
                    size="small"
                    label={t("pages.userProfile.phone.changeButton")}
                    onClick={handleChangePhone}
                    disabled={phoneSaving}
                    testId="amhp-phone-change-btn"
                    sx={{
                      "&:hover": {
                        color: "primary.main",
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    label={t("pages.userProfile.phone.removeButton")}
                    onClick={() => void handleRemovePhone()}
                    disabled={phoneSaving}
                    testId="amhp-phone-remove-btn"
                    sx={{
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}

          {phoneStep === "input" && (
            <Stack spacing={2}>
              <MuiTextField
                label={t("pages.userProfile.phone.label")}
                placeholder={t("pages.userProfile.phone.placeholder")}
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  if (phoneError) setPhoneError(null);
                }}
                error={!!phoneError}
                size="small"
                sx={{ maxWidth: 320 }}
                helperText={
                  phoneError || t("pages.userProfile.phone.helperText")
                }
                data-testid="amhp-phone-input"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={phoneConsent}
                    onChange={(e) => {
                      setPhoneConsent(e.target.checked);
                    }}
                    size="small"
                    data-testid="amhp-phone-consent-checkbox"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {t("pages.userProfile.phone.consentLabel")}
                  </Typography>
                }
                sx={{ alignItems: "center", ml: -0.5 }}
              />

              <Box>
                <Button
                  variant="contained"
                  size="small"
                  label={
                    phoneSaving
                      ? t("pages.userProfile.phone.savingButton")
                      : t("pages.userProfile.phone.saveButton")
                  }
                  onClick={() => void handleSavePhone()}
                  disabled={phoneSaving || !phoneInput || !phoneConsent}
                  testId="amhp-phone-save-btn"
                />
              </Box>
            </Stack>
          )}
        </Container>
      )}
      <Container addMarginBottom>
        <FormTitle
          useReducedTopPadding
          hasTitleBottomMargin={false}
          subtitleText={t("pages.userProfile.accreditationsSectionTitle")}
        />

        <Dropdown
          label={t("pages.userProfile.profRegDropdown.label")}
          errorMessage={errors.profRegistrations as string}
          name="profRegDropdown"
          values={[
            {
              value: PROF_REG_TYPES.GMC,
              label: t("pages.userProfile.profRegDropdown.options.gmc"),
            },
            {
              value: PROF_REG_TYPES.NMC,
              label: t("pages.userProfile.profRegDropdown.options.nmc"),
            },
            {
              value: PROF_REG_TYPES.HCPC,
              label: t("pages.userProfile.profRegDropdown.options.hcpc"),
            },
            {
              value: PROF_REG_TYPES.SWE,
              label: t("pages.userProfile.profRegDropdown.options.swe"),
            },
          ]}
          multiple
          selectedValue={
            Array.isArray(values.profRegistrations)
              ? values.profRegistrations
              : [values.profRegistrations]
          }
          onChange={(profRegistrations: string[]) => {
            const newValues = { ...values, profRegistrations };
            setValues(applyFormInvariants(newValues));
          }}
        />

        <Dropdown
          label={t("pages.userProfile.mhApprovalsDropdown.label")}
          errorMessage={errors.mhApprovals as string}
          name="mhApprovalsDropdown"
          values={[
            {
              value: APPROVAL_TYPES.AMHP,
              disabled: !derived.canBeAmhp,
              label: t("pages.userProfile.mhApprovalsDropdown.options.amhp"),
            },
            {
              value: APPROVAL_TYPES.AC,
              disabled: !values.profRegistrations.length,
              label: t("pages.userProfile.mhApprovalsDropdown.options.ac"),
              sublabel: t("pages.userProfile.mhApprovalsDropdown.info.ac"),
            },
            {
              value: APPROVAL_TYPES.S12,
              // S12 option is disabled when:
              //  - User is not a Registered Medical Practitioner (fixed as unselected)
              //  - User is a Registered Medical Practitioner with Approved Clinician status (fixed as selected)
              disabled:
                !derived.isRmp ||
                (derived.isRmp && derived.isApprovedClinician),
              label: t("pages.userProfile.mhApprovalsDropdown.options.s12"),
              sublabel: t("pages.userProfile.mhApprovalsDropdown.info.s12"),
            },
          ]}
          multiple
          selectedValue={
            Array.isArray(values.mhApprovals)
              ? values.mhApprovals
              : [values.mhApprovals]
          }
          onChange={(mhApprovals: string[]) => {
            // Unselect S12 approval if user is an RMP and AC approval is unselected. This is done because
            // for RMP's, selecting AC auto-selects S12. This isn't entirely precise as the user could also have
            // separately checked S12 themselves, but errs on the side of not selecting an approval.
            if (
              derived.isRmp &&
              derived.isApprovedClinician &&
              !mhApprovals.includes(APPROVAL_TYPES.AC)
            ) {
              mhApprovals = mhApprovals.filter((a) => a !== APPROVAL_TYPES.S12);
            }

            const newValues = { ...values, mhApprovals };
            setValues(applyFormInvariants(newValues));
          }}
        />

        <HorizontalLine topMargin />

        {derived.isRmp && (
          <TextField
            useFullWidth
            onChange={(gmcNumber: string) =>
              setValues({ ...values, gmcNumber })
            }
            name={"gmcNumber"}
            showHelperText
            errorMessage={errors.gmcNumber}
            label={t("pages.userProfile.gmcNumberLabel")}
            value={values.gmcNumber || ""}
          />
        )}

        {derived.isNurse && (
          <TextField
            useFullWidth
            onChange={(pinNumber: string) =>
              setValues({ ...values, pinNumber })
            }
            name={"pinNumber"}
            showHelperText
            errorMessage={errors.pinNumber as string}
            label={t("pages.userProfile.pinNumberLabel")}
            value={values.pinNumber || ""}
          />
        )}

        {derived.isHcpc && (
          <TextField
            useFullWidth
            onChange={(hcpcNumber: string) =>
              setValues({ ...values, hcpcNumber })
            }
            name={"hcpcNumber"}
            showHelperText
            errorMessage={errors.hcpcNumber as string}
            label={t("pages.userProfile.hcpcNumberLabel")}
            value={values.hcpcNumber || ""}
          />
        )}

        {derived.isSocialWorker && (
          <TextField
            useFullWidth
            onChange={(socialWorkerNumber: string) =>
              setValues({ ...values, socialWorkerNumber })
            }
            name={"socialWorkerNumber"}
            showHelperText
            errorMessage={errors.socialWorkerNumber as string}
            label={t("pages.userProfile.socialWorkerNumberLabel")}
            value={values.socialWorkerNumber || ""}
          />
        )}

        {derived.isAmhp && (
          <>
            <FormLabel label={t("pages.userProfile.localAuthorityFormLabel")} />
            <Typeahead
              useFullWidth
              options={localAuthorities}
              inputValue={values.localAuthority || ""}
              errorMessage={errors.localAuthority}
              optionsKey={"name"}
              name={"la"}
              getOptionLabel={(la: ManagedLocationBase | string) => {
                if (typeof la === "string") {
                  return la;
                } else {
                  return la.name;
                }
              }}
              onInputChange={(value: string | null) => {
                if (value) {
                  setValues({ ...values, localAuthority: value });
                } else {
                  setValues({ ...values, localAuthority: null! });
                }
              }}
            />
          </>
        )}

        {derived.isApprovedClinician && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& .MuiFormControl-root": { width: "100%" },
              }}
            >
              <DateFormField
                field={{
                  type: "date",
                  field: "approvedClinicianExpiryDate",
                  label: t("pages.userProfile.approvedClinicianExpiryDate"),
                  ...(derived.isRmp && {
                    minimum: (today: string) => today,
                    onAccept: handleExpiryDateAccepted,
                  }),
                }}
                fieldProps={fieldProps}
              />
              {derived.isRmp &&
                values.approvedClinicianExpiryDate &&
                !errors.approvedClinicianExpiryDate && (
                  <Tooltip
                    title={
                      values.approvedClinicianSyncedWithApprovalsApi
                        ? "Verified against approvals register. Click to re-verify."
                        : "Not verified against approvals register. Click to verify."
                    }
                  >
                    <IconButton
                      onClick={handleReverifyS12}
                      disabled={isCheckingS12}
                      size="small"
                      sx={{ p: 0.25 }}
                      aria-label={
                        values.approvedClinicianSyncedWithApprovalsApi
                          ? "Re-verify Approved Clinician approval"
                          : "Verify Approved Clinician approval"
                      }
                    >
                      {isCheckingS12 ? (
                        <CircularProgress size={18} sx={{ color: "#8ABE23" }} />
                      ) : values.approvedClinicianSyncedWithApprovalsApi ? (
                        <VerifiedUserIcon
                          sx={{ color: "#8ABE23", fontSize: 22 }}
                        />
                      ) : (
                        <ErrorOutlineIcon
                          sx={{ color: "#C74418", fontSize: 22 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
            {derived.isRmp &&
              values.approvedClinicianSyncedWithApprovalsApi && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mt: -1, display: "block" }}
                >
                  Data from NHS BSA Section 12 API (DHSC)
                </Typography>
              )}
          </Box>
        )}

        {derived.isSection12Approved &&
          !(derived.isRmp && derived.isApprovedClinician) && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& .MuiFormControl-root": { width: "100%" },
                }}
              >
                <DateFormField
                  field={{
                    type: "date",
                    field: "section12ExpiryDate",
                    label: t(
                      "pages.onBoarding.confirmOccupation.section12ExpiryDate",
                    ),
                    minimum: (today) => today,
                    onAccept: handleExpiryDateAccepted,
                  }}
                  fieldProps={fieldProps}
                />
                {values.section12ExpiryDate && !errors.section12ExpiryDate && (
                  <Tooltip
                    title={
                      values.section12SyncedWithApprovalsApi
                        ? "Verified against Section 12 register. Click to re-verify."
                        : "Not verified against Section 12 register. Click to verify."
                    }
                  >
                    <IconButton
                      onClick={handleReverifyS12}
                      disabled={isCheckingS12}
                      size="small"
                      sx={{ p: 0.25 }}
                      aria-label={
                        values.section12SyncedWithApprovalsApi
                          ? "Re-verify Section 12 approval"
                          : "Verify Section 12 approval"
                      }
                    >
                      {isCheckingS12 ? (
                        <CircularProgress size={18} sx={{ color: "#8ABE23" }} />
                      ) : values.section12SyncedWithApprovalsApi ? (
                        <VerifiedUserIcon
                          sx={{ color: "#8ABE23", fontSize: 22 }}
                        />
                      ) : (
                        <ErrorOutlineIcon
                          sx={{ color: "#C74418", fontSize: 22 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {values.section12SyncedWithApprovalsApi && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mt: -1, display: "block" }}
                >
                  Data from NHS BSA Section 12 API (DHSC)
                </Typography>
              )}
            </Box>
          )}
      </Container>
      {!isAdminUser(userContext?.user) && (
        <Container addMarginBottom>
          <Signature
            name="profileFormSignature"
            checkboxLabel={t("pages.common.signature.checkboxLabel")}
            confirmButtonLabel={t("buttonLabels.confirm")}
            clearButtonLabel={t("pages.common.signature.clearButtonLabel")}
            formSignature={values?.defaultSignature?.toString()}
            saveSignature={(e) => setValues({ ...values, defaultSignature: e })}
            userDefaultSignature={user.defaultSignature}
            hideSavedSignature
            showSaveDefaultSignatureOption={false}
          />
        </Container>
      )}
    </>
  );
};
